// SPDX-FileCopyrightText: 2026 Stadtplatz.de
//
// SPDX-License-Identifier: EUPL-1.2

import Keycloak from "keycloak-js";
import env from "../utils/env";

const keycloakConfig = {
  url: env.REACT_APP_KEYCLOAK_URL,
  realm: env.REACT_APP_KEYCLOAK_REALM,
  clientId: env.REACT_APP_KEYCLOAK_CLIENT_ID,
};

const keycloakInstance = new Keycloak(keycloakConfig);

// Keycloak runs on a different site than this app (e.g. iam.stadtplatz.de vs fair-urban-space.de)
const TOKEN_STORAGE_KEY = "kc_tokens";

const loadStoredTokens = () => {
  try {
    const raw = localStorage.getItem(TOKEN_STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return {
      token: parsed.token,
      refreshToken: parsed.refreshToken,
      idToken: parsed.idToken,
    };
  } catch {
    return {};
  }
};

export const persistTokens = (kc) => {
  try {
    if (kc?.token && kc?.refreshToken) {
      localStorage.setItem(
        TOKEN_STORAGE_KEY,
        JSON.stringify({
          token: kc.token,
          refreshToken: kc.refreshToken,
          idToken: kc.idToken,
        }),
      );
    }
  } catch (e) {
    console.warn("Could not persist Keycloak tokens:", e);
  }
};

export const clearStoredTokens = () => {
  try {
    localStorage.removeItem(TOKEN_STORAGE_KEY);
  } catch (e) {
    console.warn("Could not clear Keycloak tokens:", e);
  }
};

const decodeJwt = (jwt) => {
  try {
    const base64 = jwt.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
    return JSON.parse(atob(base64));
  } catch {
    return null;
  }
};

export const getStoredTokenClaims = () => {
  const { token } = loadStoredTokens();
  return token ? decodeJwt(token) : null;
};

export const hasUsableStoredSession = () => {
  const { token, refreshToken } = loadStoredTokens();
  if (!token || !refreshToken) return false;
  const refreshClaims = decodeJwt(refreshToken);
  if (refreshClaims?.exp) return refreshClaims.exp * 1000 > Date.now();
  return true;
};

const commonInitOptions = {
  silentCheckSsoRedirectUri: window.location.origin + "/silent-check-sso.html",
  checkLoginIframe: false,
  pkceMethod: "S256",
  enableLogging: true,
};

const buildInitOptions = () => {
  const stored = loadStoredTokens();
  if (stored.token && stored.refreshToken) {
    return { ...commonInitOptions, ...stored };
  }
  return { ...commonInitOptions, onLoad: "check-sso" };
};

let initPromise = null;
export const initKeycloakOnce = () => {
  if (!initPromise) {
    initPromise = keycloakInstance.init(buildInitOptions());
  }
  return initPromise;
};

export { keycloakInstance, keycloakConfig };
